"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.MinnesotaRules = exports.MN_THRESHOLDS = void 0;
const browser_1 = require("encamp-prisma/generated/browser");
const wasteGeneratorStatusThresholds_1 = require("encamp-shared/dist/constants/wasteGeneratorStatusThresholds");
const isNonEmpty_1 = require("encamp-shared/dist/utils/isNonEmpty");
const _FEDERAL_1 = require("./_FEDERAL");
const _stateRules_1 = require("./_stateRules");
/**
 * Minnesota (MPCA) accumulation limits, Minn. R. 7045.0292, verified against
 * MPCA fact sheet w-hw1-05 (Sept 2021).
 *
 * Minnesota's clocks are the federal numbers with one deep structural
 * difference: a Minnesota VSQG has NO standing central clock at all. Its
 * constraint is a 1,000 kg on-site quantity cap (subp. 6), and exceeding it
 * makes the facility an SQG (subp. 6 item H) — a facility-level
 * quantity trigger the per-container clock cannot express, owned by the
 * on-site cap rollup rather than this class.
 *
 * Minnesota sets its clocks and caps by the MORE STRINGENT of the
 * federal generator status and Minnesota's own determination (see
 * `effectiveStatus`). Minnesota's own tier vocabulary additionally includes
 * the MiniQG (an MPCA licensing subset of VSQG), which this class must
 * interpret.
 */
const MN_STATE_STATUS_DOMAIN = [
    "LARGE_QUANTITY_GENERATOR",
    "SMALL_QUANTITY_GENERATOR",
    "VERY_SMALL_QUANTITY_GENERATOR",
    "MINI_QUANTITY_GENERATOR",
];
const MN_SATELLITE_SOURCE = {
    jurisdiction: "MN",
    citation: "Minn. R. 7045.0292, subp. 8",
};
/** Federal rules under Minnesota's regulations. */
exports.MN_THRESHOLDS = {
    centralTimeLimit: {
        ..._FEDERAL_1.FEDERAL_THRESHOLDS.centralTimeLimit,
        source: { jurisdiction: "MN", citation: "Minn. R. 7045.0292" },
    },
    /**
     * Minnesota's own used-oil generator standards set no accumulation time
     * limit, mirroring part 279, and recycled used oil is subject only to the
     * used-oil parts, which impose no satellite caps.
     */
    usedOilTimeLimit: {
        ..._FEDERAL_1.FEDERAL_THRESHOLDS.usedOilTimeLimit,
        source: { jurisdiction: "MN", citation: "Minn. R. 7045.0125, subp. 3a; 7045.0855, subp. 2" },
    },
    capNonAcuteVolume: { ..._FEDERAL_1.FEDERAL_THRESHOLDS.capNonAcuteVolume, source: MN_SATELLITE_SOURCE },
    capAcuteVolume: { ..._FEDERAL_1.FEDERAL_THRESHOLDS.capAcuteVolume, source: MN_SATELLITE_SOURCE },
    capAcuteMass: { ..._FEDERAL_1.FEDERAL_THRESHOLDS.capAcuteMass, source: MN_SATELLITE_SOURCE },
    satelliteToCentralMoveWindow: {
        ..._FEDERAL_1.FEDERAL_THRESHOLDS.satelliteToCentralMoveWindow,
        source: MN_SATELLITE_SOURCE,
    },
    onSiteNonAcute: {
        ..._FEDERAL_1.FEDERAL_THRESHOLDS.onSiteNonAcute,
        source: { jurisdiction: "MN", citation: "Minn. R. 7045.0292" },
    },
    onSiteAcute: {
        ..._FEDERAL_1.FEDERAL_THRESHOLDS.onSiteAcute,
        source: { jurisdiction: "MN", citation: "Minn. R. 7045.0292, subp. 7" },
    },
};
/** Minnesota (MPCA) accumulation limits, Minn. R. 7045.0292. */
class MinnesotaRules extends _stateRules_1.StateRules {
    constructor() {
        super(...arguments);
        this.stateStatusDomain = MN_STATE_STATUS_DOMAIN;
        this.adoptsEpisodicEvents = false;
    }
    /** The effective Minnesota status, named in the federal vocabulary. */
    describeGeneratorStatus(facility) {
        const effective = this.effectiveStatus(facility);
        return effective.value === undefined
            ? null
            : _FEDERAL_1.FEDERAL_READABLE_GENERATOR_STATUSES[effective.value];
    }
    centralTimeLimit(situation) {
        const effective = this.effectiveStatus(situation.facility);
        const basis = this.statusBasis(situation.facility);
        if (effective.value === undefined) {
            return {
                ...exports.MN_THRESHOLDS.centralTimeLimit,
                determination: "UNDETERMINED",
                missing: effective.missing,
                basis,
            };
        }
        switch (effective.value) {
            case "LQG":
                return {
                    ...exports.MN_THRESHOLDS.centralTimeLimit,
                    source: { jurisdiction: "MN", citation: "Minn. R. 7045.0292, subp. 1" },
                    determination: "KNOWN",
                    basis,
                    value: 90,
                };
            case "SQG": {
                const farTransport = situation.container.mustTransport200Miles;
                if (farTransport === undefined) {
                    return {
                        ...exports.MN_THRESHOLDS.centralTimeLimit,
                        source: { jurisdiction: "MN", citation: "Minn. R. 7045.0292, subps. 5, 9" },
                        determination: "UNDETERMINED",
                        missing: ["container.mustTransport200Miles"],
                        basis,
                    };
                }
                const source = {
                    jurisdiction: "MN",
                    citation: farTransport ? "Minn. R. 7045.0292, subp. 9" : "Minn. R. 7045.0292, subp. 5",
                };
                return {
                    ...exports.MN_THRESHOLDS.centralTimeLimit,
                    source,
                    determination: "KNOWN",
                    basis: { ...basis, container: { mustTransport200Miles: farTransport } },
                    value: farTransport ? 270 : 180,
                };
            }
            case "VSQG":
                // Subp. 6 caps a VSQG by quantity instead, which the on-site thresholds carry.
                return {
                    ...exports.MN_THRESHOLDS.centralTimeLimit,
                    source: { jurisdiction: "MN", citation: "Minn. R. 7045.0292, subp. 6" },
                    determination: "EXEMPT",
                    basis,
                };
        }
    }
    /** Minnesota never adopted subpart L, so no container takes the episodic window. */
    containerThresholds(situation) {
        const program = this.governingProgram(situation.container);
        // A satellite container's move window reports a missing program itself.
        if (program.value === undefined && situation.area.type !== "SATELLITE") {
            const missing = [
                ...program.missing,
                ...(situation.area.type === undefined ? ["area.type"] : []),
            ];
            return [
                { ...exports.MN_THRESHOLDS.centralTimeLimit, determination: "UNDETERMINED", missing, basis: {} },
            ];
        }
        switch (program.value) {
            case "UNIVERSAL_WASTE":
                return [{ ..._FEDERAL_1.FEDERAL_THRESHOLDS.universalWasteTimeLimit, determination: "KNOWN" }];
            case "USED_OIL":
                return [{ ...exports.MN_THRESHOLDS.usedOilTimeLimit, determination: "EXEMPT" }];
            case "PHARMACEUTICAL":
            case "RCRA":
                break;
        }
        switch (situation.area.type) {
            case "CENTRAL":
                return [this.centralTimeLimit(situation)];
            case "SATELLITE": {
                const caps = this.satelliteCaps();
                return [
                    ..._FEDERAL_1.Federal.satelliteMoveWindow(this, exports.MN_THRESHOLDS.satelliteToCentralMoveWindow, caps, situation.container),
                ];
            }
            case undefined:
                return [
                    {
                        ...exports.MN_THRESHOLDS.centralTimeLimit,
                        determination: "UNDETERMINED",
                        missing: ["area.type"],
                        basis: {},
                    },
                ];
        }
    }
    /**
     * The stricter of the federal status and Minnesota's own (Minn. R.
     * 7045.0206), which counts a MiniQG as a VSQG (Metro County's stricter
     * limits are not modeled). LQG on either settles it; otherwise both must be
     * known.
     */
    effectiveStatus(facility) {
        const federal = facility.federalGeneratorStatus;
        const state = (0, _stateRules_1.narrowStateStatus)(facility, MN_STATE_STATUS_DOMAIN);
        if (federal === "LQG" || state === "LARGE_QUANTITY_GENERATOR") {
            return { value: "LQG" };
        }
        const missing = [
            ...(federal === undefined ? ["facility.federalGeneratorStatus"] : []),
            ...(state === undefined ? ["facility.stateGeneratorStatus"] : []),
        ];
        if ((0, isNonEmpty_1.isNonEmpty)(missing)) {
            return { missing };
        }
        if (federal === "SQG" || state === "SMALL_QUANTITY_GENERATOR") {
            return { value: "SQG" };
        }
        return { value: "VSQG" };
    }
    /** The generator statuses the thresholds read, as supplied. */
    statusBasis(facility) {
        const federal = facility.federalGeneratorStatus;
        const state = (0, _stateRules_1.narrowStateStatus)(facility, MN_STATE_STATUS_DOMAIN);
        if (federal === undefined && state === undefined) {
            return {};
        }
        return {
            facility: {
                ...(federal === undefined ? {} : { federalGeneratorStatus: federal }),
                ...(state === undefined ? {} : { stateGeneratorStatus: state }),
            },
        };
    }
    /**
     * Minnesota never adopted Subpart P, so pharmaceutical waste is ordinary
     * waste. Universal waste and used oil are the federal programs, though
     * Minnesota's own used-oil rules supply the citations.
     */
    governingProgram(container) {
        if (container.wasteKind === "PHARMACEUTICAL") {
            return { value: "RCRA" };
        }
        return _FEDERAL_1.Federal.governingProgram(container);
    }
    /**
     * Federal numbers under Minnesota's own regulation, reaching every tier:
     * satellite status is what exempts waste from the VSQG's 1,000 kg count, so
     * the caps bind VSQGs and MiniQGs too. The acute mass figure is the federal
     * stand-in for Minnesota's quart-only limit, over-strict and never
     * under-strict. They apply simultaneously; crossing one starts its move
     * window.
     */
    satelliteCaps() {
        return [
            {
                ...exports.MN_THRESHOLDS.capNonAcuteVolume,
                determination: "KNOWN",
                basis: {},
            },
            {
                ...exports.MN_THRESHOLDS.capAcuteVolume,
                determination: "KNOWN",
                basis: {},
            },
            {
                ...exports.MN_THRESHOLDS.capAcuteMass,
                determination: "KNOWN",
                basis: {},
            },
        ];
    }
    areaThresholds(situation) {
        switch (situation.area.type) {
            case "CENTRAL":
                return [];
            case "SATELLITE":
                return this.satelliteCaps();
            case undefined:
                return [
                    {
                        ...exports.MN_THRESHOLDS.capNonAcuteVolume,
                        determination: "UNDETERMINED",
                        missing: ["area.type"],
                        basis: {},
                    },
                    {
                        ...exports.MN_THRESHOLDS.capAcuteVolume,
                        determination: "UNDETERMINED",
                        missing: ["area.type"],
                        basis: {},
                    },
                    {
                        ...exports.MN_THRESHOLDS.capAcuteMass,
                        determination: "UNDETERMINED",
                        missing: ["area.type"],
                        basis: {},
                    },
                ];
        }
    }
    /**
     * Minnesota's on-site thresholds, keyed on the effective Minnesota status.
     * Two departures from federal: the SQG threshold is 3,000 kg of non-acute
     * waste rather than a 6,000 kg total, and acute waste is limited at 1 kg for
     * SQGs and VSQGs alike. A VSQG over its non-acute threshold becomes an SQG
     * (subp. 6 item H). Known gap: acute over 1 kg puts the generator under
     * subpart 1's LQG conditions without changing its status (subp. 7 item B).
     * An unknown status gets both thresholds, undetermined. Universal waste and
     * recycled used oil are outside this chapter's general requirements
     * (7045.0120 subp. 2, 7045.0125 subp. 3a) and count toward neither.
     */
    facilityThresholds(facility) {
        const effective = this.effectiveStatus(facility);
        const basis = this.statusBasis(facility);
        if (effective.value === undefined) {
            return [
                {
                    ...exports.MN_THRESHOLDS.onSiteNonAcute,
                    determination: "UNDETERMINED",
                    missing: effective.missing,
                    basis,
                    consequences: [{ kind: "COMPLIANCE_NOT_RECORDED" }],
                },
                {
                    ...exports.MN_THRESHOLDS.onSiteAcute,
                    determination: "UNDETERMINED",
                    missing: effective.missing,
                    basis,
                },
            ];
        }
        const acute = {
            ...exports.MN_THRESHOLDS.onSiteAcute,
            determination: "KNOWN",
            basis,
            value: wasteGeneratorStatusThresholds_1.MN_ACCUMULATION_THRESHOLD_ACUTE_KG,
        };
        switch (effective.value) {
            case "LQG":
                return [];
            case "SQG":
                return [
                    {
                        ...exports.MN_THRESHOLDS.onSiteNonAcute,
                        source: { jurisdiction: "MN", citation: "Minn. R. 7045.0292, subps. 5, 11" },
                        determination: "KNOWN",
                        basis,
                        value: wasteGeneratorStatusThresholds_1.MN_SQG_ACCUMULATION_THRESHOLD_KG,
                        // Known gap: over the cap, subp. 11 makes the generator a storage
                        // facility operator; nothing records or surfaces that yet.
                        consequences: [{ kind: "COMPLIANCE_NOT_RECORDED", actionToComply: "SHIP" }],
                    },
                    acute,
                ];
            case "VSQG":
                return [
                    {
                        ...exports.MN_THRESHOLDS.onSiteNonAcute,
                        source: { jurisdiction: "MN", citation: "Minn. R. 7045.0292, subp. 6" },
                        determination: "KNOWN",
                        basis,
                        value: wasteGeneratorStatusThresholds_1.MN_VSQG_ACCUMULATION_THRESHOLD_KG,
                        consequences: [
                            {
                                kind: "GENERATOR_STATUS_OUTCOME_NOT_RECORDED",
                                to: _FEDERAL_1.FEDERAL_READABLE_GENERATOR_STATUSES.SQG,
                            },
                        ],
                    },
                    acute,
                ];
        }
    }
    accumulationCategory(container) {
        // STATE_REGULATED is non-acute pending the waste team's per-state decision.
        return container.classification === browser_1.WasteClassification.STATE_REGULATED
            ? { value: "NON_ACUTE" }
            : _FEDERAL_1.Federal.category(container);
    }
}
exports.MinnesotaRules = MinnesotaRules;
//# sourceMappingURL=MN.js.map